import React from 'react';
import { Link } from 'react-router-dom';
import { PageShell } from './layout';
import styles from '../styles/ChiSiamo.module.css';
import FaIcon from './FaIcon';

// Team member data
const TEAM_MEMBERS = [
  {
    id: 'chad',
    name: 'Chad Lieberman',
    role: "Sviluppatore & Ingegnere IA",
    emoji: '👨‍💻',
    gradient: 'linear-gradient(145deg, #4f46e5 0%, #7c3aed 100%)',
    accentColor: '#7c3aed',
    description: (
      <>
        <p>
          Chad è il mago dietro la tecnologia dei Giochini. Con un background in 
          intelligenza artificiale e sviluppo software, ha costruito l'intera 
          piattaforma da zero.
        </p>
        <p>
          Studia l'italiano prevalentemente da autoimprenditore (autodidatta!) 
          da oltre 3 anni e, proprio questa esperienza di apprendimento, lo 
          ha spinto alla creazione di strumenti che rendono lo studio più 
          divertente ed efficace.
        </p>
      </>
    ),
    tags: ['AI/ML', 'Full-Stack', 'UX Design', 'Studente di Italiano']
  },
  {
    id: 'marco',
    name: 'Marco Residori',
    role: 'Esperto di Glottodidattica',
    emoji: '🧙‍♂️',
    gradient: 'linear-gradient(145deg, #059669 0%, #10b981 100%)',
    accentColor: '#10b981',
    description: (
      <>
        <p>
          Marco ha alle spalle oltre 10 anni di esperienza nell'insegnamento dell'italiano a stranieri.
          La sua profonda conoscenza della glottodidattica guida il design pedagogico di ogni gioco.
        </p>
        <p>
          Inoltre, è lui che scrive le newsletter e tiene viva la community. 
          La sua esperienza con studenti di tutti i livelli e provenienze 
          assicura che i nostri giochi non siano solo divertenti, ma anche 
          didatticamente efficaci e adatti a diversi stili e livelli di apprendimento.
        </p>
      </>
    ),
    tags: ['Glottodidattica', 'Italiano L2/LS', 'Newsletter', 'Pedagogia']
  },
  {
    id: 'mario',
    name: 'Mario Giochini',
    role: 'Robot Creativo & Community Manager',
    emoji: '🤖',
    gradient: 'linear-gradient(145deg, #f59e0b 0%, #ef4444 100%)',
    accentColor: '#f59e0b',
    description: (
      <>
        <p>
          Mario è il nostro instancabile robot AI! Lavora giorno e notte per 
          creare nuovi puzzle, inventare collegamenti creativi tra parole  
          e assicurarsi che ogni giorno ci siano sfide avvincenti per tutti.
        </p>
        <p>
          È anche lui a rispondere (con un po&apos; di aiuto umano) ai feedback degli utenti. 
          Se qualcosa non va, Mario vuole saperlo!
        </p>
      </>
    ),
    tags: ['Content Creation', 'Puzzle Design', 'Community', 'AI']
  },
  {
    id: 'lella',
    name: 'Lella',
    role: 'Compagna di studio per studenti di italiano',
    avatar: '/lella.png',
    gradient: 'linear-gradient(145deg, #7c3aed 0%, #8b5cf6 100%)',
    accentColor: '#8b5cf6',
    description: (
      <>
        <p>
          Lella è la tua compagna di studio: un assistente AI pensato per chi impara 
          l&apos;italiano. Dopo aver completato una partita, tocca una parola e lei te la 
          spiega in contesto—non solo la definizione, ma perché è usata così, 
          con esempi pratici e curiosità linguistiche.
        </p>
        <p>
          Le sue spiegazioni sono chiare e adatte a studenti di ogni livello. 
          Trasforma ogni partita in un&apos;occasione per approfondire vocabolario 
          e comprensione della lingua.
        </p>
      </>
    ),
    tags: ['Spiegazioni in contesto', 'Apprendimento', 'Italiano L2', 'AI']
  }
];

// Genesis story sections
const GENESIS_STORY = [
  {
    icon: '💡',
    title: 'L\'idea',
    text: 'Tutto è iniziato dalla frustrazione di non trovare strumenti divertenti per praticare l\'italiano quotidianamente. Esistono app per imparare le basi, ma mancava qualcosa per mantenere viva la lingua con sfide creative.'
  },
  {
    icon: '🎮',
    title: 'I giochi',
    text: 'Ci siamo ispirati a popolari formati di giochi di parole, adattandoli per chi studia italiano. Ogni gioco è progettato per allenare aspetti diversi: vocabolario, ortografia, associazioni semantiche, memoria.'
  },
  {
    icon: '🤝',
    title: 'Le collaborazioni',
    text: (
      <>
        Abbiamo stretto partnership con insegnanti di italiano e linguatuber che condividono 
        la nostra passione per un apprendimento divertente e stimolante. Insieme, raggiungiamo più studenti 
        e creiamo contenuti sempre più coinvolgenti. <Link to="/collaborazioni">Vuoi unirti a noi?</Link>
      </>
    )
  },
  {
    icon: '🌱',
    title: 'La crescita',
    text: 'Da semplice esperimento, Giochini di Parole è diventato un punto di riferimento per migliaia di studenti di italiano in tutto il mondo. E continuiamo a crescere, aggiungendo nuovi giochi e funzionalità.'
  },

];

const ChiSiamo = () => {
  return (
    <PageShell>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        {/* Decorative background elements */}
        <div className={styles.bgPattern}>
          {[...Array(20)].map((_, i) => (
            <div key={i} className={styles.bgLetter}>
              {['A', 'B', 'C', 'È', 'Ì', 'Ò', 'Ù', 'Z', 'P', 'L', 'M', 'N', 'R', 'S', 'T', 'V', 'D', 'F', 'G', 'H'][i]}
            </div>
          ))}
        </div>

        {/* Floating accents */}
        <div className={styles.floatingAccents}>
          {['à', 'è', 'é', 'ì', 'ò', 'ù'].map((accent, i) => (
            <span key={i} className={styles.floatingAccent}>{accent}</span>
          ))}
        </div>

        <div className={styles.heroContent}>
          <div className={styles.heroEmoji}>👋</div>
          <h1 className={styles.heroTitle}>Chi Siamo</h1>
          <p className={styles.heroSubtitle}>
            Dietro ogni gioco c'è una piccola squadra con una grande passione: 
            rendere lo studio dell'italiano un piacere quotidiano.
          </p>
        </div>

        <div className={styles.scrollHint}>
          <span>Scopri la nostra storia</span>
          <FaIcon className="fas fa-chevron-down"  />
        </div>
      </section>

      {/* Genesis Story Section */}
      <section className={styles.genesisSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>🚀</span>
            <h2 className={styles.sectionTitle}>La nostra storia</h2>
            <p className={styles.sectionSubtitle}>
              Come è nato Giochini di Parole
            </p>
          </div>

          <div className={styles.genesisTimeline}>
            {GENESIS_STORY.map((item, index) => (
              <div key={index} className={styles.genesisItem}>
                <div className={styles.genesisIcon}>{item.icon}</div>
                <div className={styles.genesisContent}>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
                {index < GENESIS_STORY.length - 1 && (
                  <div className={styles.genesisConnector} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className={styles.teamSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>🎭</span>
            <h2 className={styles.sectionTitle}>Il Team</h2>
            <p className={styles.sectionSubtitle}>
              Le menti (e i robot) dietro i Giochini
            </p>
          </div>

          <div className={styles.teamGrid}>
            {TEAM_MEMBERS.map((member, index) => (
              <article 
                key={member.id} 
                className={styles.teamCard}
                style={{ '--accent-color': member.accentColor, animationDelay: `${index * 0.15}s` }}
              >
                <div 
                  className={styles.cardHeader}
                  style={{ background: member.gradient }}
                >
                  {member.avatar ? (
                    <img src={member.avatar} alt={member.name} className={styles.memberAvatar} />
                  ) : (
                    <span className={styles.memberEmoji}>{member.emoji}</span>
                  )}
                  <div className={styles.cardHeaderPattern} />
                </div>
                
                <div className={styles.cardBody}>
                  <h3 className={styles.memberName}>{member.name}</h3>
                  <p className={styles.memberRole}>{member.role}</p>
                  
                  <div className={styles.memberDescription}>
                    {member.description}
                  </div>
                  
                  <div className={styles.memberTags}>
                    {member.tags.map((tag, i) => (
                      <span key={i} className={styles.tag}>{tag}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className={styles.missionSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.missionContent}>
            <div className={styles.missionIcon}>🎯</div>
            <h2 className={styles.missionTitle}>La nostra missione</h2>
            <p className={styles.missionText}>
              Vogliamo che imparare l'italiano sia un <em>piacere</em>, non un dovere. 
              Per questo creiamo giochi che si possono risolvere in pochi minuti ogni giorno, 
              ma che lasciano un segno duraturo nel vocabolario e nella comprensione della lingua.
            </p>
            <p className={styles.missionText}>
              Crediamo che la <strong>costanza</strong> batta l'intensità, e che 5 minuti al giorno 
              valgano più di un'ora ogni tanto. I nostri giochi sono progettati per diventare 
              un'abitudine quotidiana, come il caffè della mattina, ma per il cervello.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className={styles.valuesSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>💫</span>
            <h2 className={styles.sectionTitle}>I nostri valori</h2>
          </div>

          <div className={styles.valuesGrid}>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>🎨</div>
              <h3>Creatività</h3>
              <p>Ogni gioco è un'opportunità per pensare in modo diverso e creare connessioni inaspettate.</p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>🤝</div>
              <h3>Accessibilità</h3>
              <p>I giochi sono gratuiti e pensati per studenti di tutti i livelli, dal principiante all'avanzato.</p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>🔬</div>
              <h3>Efficacia</h3>
              <p>Ogni meccanica di gioco è progettata con principi di apprendimento evidence-based.</p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>🎉</div>
              <h3>Divertimento</h3>
              <p>Se non è divertente, non funziona. Il gioco è il modo più naturale per imparare.</p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>👥</div>
              <h3>Comunità</h3>
              <p>Imparare insieme è più bello. Condividi i tuoi progressi e sfida i tuoi amici.</p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>🔍</div>
              <h3>Curiosità</h3>
              <p>Ogni parola ha una storia. Coltiviamo la voglia di scoprire e esplorare la lingua.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <span className={styles.ctaEmoji}>💌</span>
          <h2 className={styles.ctaTitle}>Rimaniamo in contatto!</h2>
          <p className={styles.ctaText}>
            Hai idee, suggerimenti o semplicemente vuoi farci un saluto? 
            Ci farebbe piacere sentirti!
          </p>
          <div className={styles.ctaButtons}>
            <Link to="/newsletter" className={styles.ctaButtonPrimary}>
              <FaIcon className="fas fa-envelope"  />
              <span>Iscriviti alla newsletter</span>
            </Link>
            <Link to="/contatti" className={styles.ctaButtonSecondary}>
              <FaIcon className="fas fa-paper-plane"  />
              <span>Scrivici</span>
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default ChiSiamo;
